import * as B from '@blueprintjs/core';
import React from 'react';
import {ConnectDragSource, ConnectDropTarget, useDrag, useDrop} from 'react-dnd';
import ReactDOM from 'react-dom';

import {LegendUIContext} from 'app/components/DeclarativeMap/LegendUIContext';
import * as hookUtils from 'app/utils/hookUtils';

import MapOverlayDialog from './MapOverlayDialog';

const DRAG_AROUND_TYPE = 'target';

interface Position {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}
interface DragAroundProps {
  initialPosition: Position;
}

type DragItem = {type: typeof DRAG_AROUND_TYPE} & Position;

type MapDialogOverlayProps = React.ComponentProps<typeof MapOverlayDialog>;
type Props = MapDialogOverlayProps &
  DragAroundProps & {
    isCurrentlyDraggable?: boolean;
  };

interface Result {
  drag: ConnectDragSource;
  drop: ConnectDropTarget;
  position: Position;
  isDragging: boolean;
}

function useDragAround({initialPosition}: DragAroundProps): Result {
  const [position, setPosition] = React.useState<Position>(initialPosition);
  // Once the user has dragged, parent-supplied initialPosition changes stop
  // overriding their position. Before drag, we still follow initialPosition
  // so the panel shifts when the sidebar opens or the analyze-polygon
  // popup appears.
  const hasUserDraggedRef = React.useRef(false);
  const stableInitial = hookUtils.useContinuity(initialPosition);
  React.useEffect(() => {
    if (!hasUserDraggedRef.current) setPosition(stableInitial);
  }, [stableInitial]);

  const [{isDragging}, drag] = useDrag({
    type: DRAG_AROUND_TYPE,
    // Function form so the current `position` is read when drag starts, not
    // baked into the config on every render.
    item: () => ({type: DRAG_AROUND_TYPE, ...position}),
    collect: (monitor) => ({isDragging: monitor.isDragging()}),
  });

  const [, drop] = useDrop({
    accept: DRAG_AROUND_TYPE,
    drop(item: DragItem, monitor) {
      const delta = monitor.getDifferenceFromInitialOffset();
      if (!delta) return;
      // `!= null` (not truthy) so a starting position of 0 still updates.
      const top = item.top != null ? Math.round(item.top + delta.y) : undefined;
      const left = item.left != null ? Math.round(item.left + delta.x) : undefined;
      const right = item.right != null ? Math.round(item.right - delta.x) : undefined;
      const bottom = item.bottom != null ? Math.round(item.bottom - delta.y) : undefined;
      hasUserDraggedRef.current = true;
      setPosition({top, right, bottom, left});
    },
  });

  return {drag, drop, position, isDragging};
}

const DraggableMapOverlayDialog: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  initialPosition,
  isCurrentlyDraggable = true,
  children,
  additionalButtons,
  ...props
}) => {
  const {isLegendCollapsed, setIsLegendCollapsed} = React.useContext(LegendUIContext);
  const {drag, drop, position, isDragging} = useDragAround({initialPosition});

  return (
    <>
      <MapOverlayDialog
        {...props}
        additionalButtons={[
          additionalButtons,
          <B.Button
            key="toggleCollapse"
            icon={isLegendCollapsed ? 'maximize' : 'minimize'}
            minimal
            onClick={() => {
              setIsLegendCollapsed((prev) => !prev);
            }}
          />,
        ]}
        ref={isCurrentlyDraggable ? drag : undefined}
        style={{
          ...position,
          ...props.style,
          display: isDragging ? 'none' : 'block',
        }}
        contentStyles={
          isLegendCollapsed
            ? {
                padding: 0,
              }
            : {}
        }
        titleBarStyle={{cursor: 'grab'}}
      >
        {!isLegendCollapsed && children}
      </MapOverlayDialog>
      {isDragging &&
        ReactDOM.createPortal(
          <div
            ref={isCurrentlyDraggable ? drop : undefined}
            // Mounted under <body> so `position: fixed` always covers the full
            // screen — a transformed/filtered parent would otherwise shrink it.
            style={{position: 'fixed', top: 0, right: 0, bottom: 0, left: 0}}
          />,
          document.body
        )}
    </>
  );
};

export default DraggableMapOverlayDialog;
